/**
 * Drives the InfinitePay checkout page (sections/infinitepay-checkout-page.liquid,
 * rendered by templates/page.checkout.json - see settings.infinitepay_checkout_page_url).
 * Loaded only on that page, unlike the old modal version which had to load
 * globally because its trigger button lived on the cart page/drawer.
 *
 * Section 1 (Identificação) and Section 2 (Entrega) are <details> elements
 * inside ONE <form>, so a single FormData read at final submit picks up
 * every field regardless of which section it's in. Section 2's address
 * fields (everything after CEP) start `disabled` as well as hidden, which
 * both keeps them out of the DOM's rendered "address" area until there's a
 * CEP to base it on, and - just as importantly - excludes them from
 * `form.reportValidity()` while still empty, so validating "Ir para
 * entrega" (which only wants Section 1's fields checked) doesn't also
 * demand an address that hasn't been reached yet.
 */

const FORM_SELECTOR = '#infinitepay-checkout-form';
const ADDRESS_STORAGE_KEY = 'infinitepay:last-address';

async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('failed to read cart');
  return response.json();
}

function showError(form, message) {
  const errorEl = form.querySelector('[data-checkout-error]');
  if (!errorEl) return;
  errorEl.textContent = message;
  errorEl.hidden = false;
}

function clearError(form) {
  const errorEl = form.querySelector('[data-checkout-error]');
  if (!errorEl) return;
  errorEl.hidden = true;
}

function setSubmitLoading(form, loading) {
  // Lives in the <aside> order summary, outside the <form> - linked to it via
  // the `form` attribute instead of being a descendant - so it's looked up
  // from the document, not from `form`.
  const submitButton = document.getElementById('infinitepay-submit-payment-summary');
  if (!submitButton) return;
  submitButton.disabled = loading;
  const label = submitButton.querySelector('.button-text');
  if (!label) return;
  if (loading) {
    submitButton.dataset.originalLabel = submitButton.dataset.originalLabel || label.textContent;
    label.textContent = Theme.translations.infinitepay_processing;
  } else if (submitButton.dataset.originalLabel) {
    label.textContent = submitButton.dataset.originalLabel;
  }
}

/* ---------- persisted address (returning shopper convenience) ---------- */

function prefillForm(form) {
  try {
    const saved = JSON.parse(localStorage.getItem(ADDRESS_STORAGE_KEY) || 'null');
    if (!saved) return;
    for (const [key, value] of Object.entries(saved)) {
      const field = form.elements.namedItem(key);
      if (field && 'value' in field && !field.value) field.value = value;
    }
  } catch {
    // ignore malformed/unavailable storage
  }
}

function saveForm(data) {
  try {
    localStorage.setItem(ADDRESS_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors (private browsing, quota, etc.)
  }
}

/* ---------------------------- CPF (mask + checksum) ---------------------------- */

function maskCpf(rawValue) {
  const digits = rawValue.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function isValidCpf(rawValue) {
  const cpf = rawValue.replace(/\D/g, '');
  // Rejects all-same-digit numbers (000.000.000-00, 111.111.111-11, ...) -
  // they pass the checksum below but are never real CPFs.
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const checkDigit = (length) => {
    let sum = 0;
    for (let i = 0; i < length; i++) sum += Number(cpf[i]) * (length + 1 - i);
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return checkDigit(9) === Number(cpf[9]) && checkDigit(10) === Number(cpf[10]);
}

function initCpfField(form) {
  const cpfField = form.elements.namedItem('cpf');
  if (!cpfField) return;

  cpfField.addEventListener('input', () => {
    cpfField.value = maskCpf(cpfField.value);
    cpfField.setCustomValidity('');
  });

  cpfField.addEventListener('blur', () => {
    if (cpfField.value && !isValidCpf(cpfField.value)) {
      cpfField.setCustomValidity(Theme.translations.infinitepay_cpf_invalid);
    } else {
      cpfField.setCustomValidity('');
    }
  });
}

/* ---------------------------- CEP -> endereço ---------------------------- */

async function lookupCep(rawCep) {
  const digits = rawCep.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.erro) return null;
    return data;
  } catch {
    return null;
  }
}

function setDeliveryDetailsEnabled(form, enabled) {
  const wrapper = form.querySelector('[data-delivery-details]');
  if (!wrapper) return;
  wrapper.hidden = !enabled;
  // Scoped to the real address inputs (.checkout-field__input) rather than
  // a blanket `input` selector, so the shipping radio - always disabled,
  // it's a display-only stand-in since there's only ever one option - isn't
  // re-enabled along with everything else.
  wrapper.querySelectorAll('.checkout-field__input').forEach((el) => {
    el.disabled = !enabled;
  });

  // The submit button lives outside the form/wrapper (in the order summary
  // aside), so it needs this gating done explicitly - it can't be used to
  // submit before there's an address to submit.
  const summarySubmitButton = document.getElementById('infinitepay-submit-payment-summary');
  if (summarySubmitButton) summarySubmitButton.disabled = !enabled;
}

async function handleCepInput(form) {
  const cepField = form.elements.namedItem('cep');
  const cepError = form.querySelector('[data-cep-error]');
  if (!cepField) return;

  const data = await lookupCep(cepField.value);

  if (!data) {
    setDeliveryDetailsEnabled(form, false);
    if (cepError) cepError.hidden = cepField.value.replace(/\D/g, '').length !== 8;
    return;
  }

  if (cepError) cepError.hidden = true;

  // The CEP the customer just typed is the source of truth for these
  // fields, so a successful lookup always overwrites them - including
  // stale values left over from a previously saved address. Only skip a
  // field when ViaCEP itself has nothing for it (some CEPs cover a whole
  // area and return an empty logradouro/bairro), so we don't erase a
  // manual edit with a blank the API didn't actually provide.
  const streetField = form.elements.namedItem('street');
  const neighborhoodField = form.elements.namedItem('neighborhood');
  const cityField = form.elements.namedItem('city');
  const stateField = form.elements.namedItem('state');
  if (streetField && data.logradouro) streetField.value = data.logradouro;
  if (neighborhoodField && data.bairro) neighborhoodField.value = data.bairro;
  if (cityField) cityField.value = data.localidade || '';
  if (stateField) stateField.value = data.uf || '';

  setDeliveryDetailsEnabled(form, true);

  const numberField = form.elements.namedItem('number');
  if (numberField instanceof HTMLInputElement && !numberField.value) numberField.focus();
}

function initCepField(form) {
  const cepField = form.elements.namedItem('cep');
  if (!cepField) return;

  let debounce;
  cepField.addEventListener('input', () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => handleCepInput(form), 400);
  });
  // Backstop for browsers/autofill that set the value without a proper
  // `input` event trail.
  cepField.addEventListener('change', () => handleCepInput(form));
}

/* ---------------------------- Seção 1 -> Seção 2 ---------------------------- */

function initSectionNavigation(form) {
  const identificationDetails = form.querySelector('#infinitepay-section-identification');
  const deliveryDetails = form.querySelector('#infinitepay-section-delivery');
  const goToDeliveryButton = form.querySelector('#infinitepay-go-to-delivery');
  if (!identificationDetails || !deliveryDetails || !goToDeliveryButton) return;

  // Kept disabled until Section 1 passes, so an early form.reportValidity()
  // call (or a stray Enter keypress) can't demand an address that isn't
  // reachable yet.
  deliveryDetails.querySelectorAll('.checkout-field__input').forEach((el) => {
    el.disabled = true;
  });
  const cepField = form.elements.namedItem('cep');
  if (cepField) cepField.disabled = true;

  goToDeliveryButton.addEventListener('click', () => {
    if (!form.reportValidity()) return;

    const name = form.elements.namedItem('name')?.value || '';
    const email = form.elements.namedItem('email')?.value || '';
    const summaryEl = identificationDetails.querySelector('[data-section-summary]');
    if (summaryEl) summaryEl.textContent = [name, email].filter(Boolean).join(' — ');
    identificationDetails.classList.add('is-complete');

    identificationDetails.open = false;
    deliveryDetails.open = true;
    if (cepField instanceof HTMLElement) {
      cepField.disabled = false;
      cepField.focus();
    }
  });
}

/* ---------------------------- Envio final ---------------------------- */

async function submitCheckout(form) {
  const formData = new FormData(form);
  const customer = {
    name: formData.get('name'),
    email: formData.get('email'),
    cpf: formData.get('cpf'),
    phone: formData.get('phone'),
  };
  const address = {
    cep: formData.get('cep'),
    street: formData.get('street'),
    number: formData.get('number'),
    neighborhood: formData.get('neighborhood'),
    complement: formData.get('complement') || '',
    city: formData.get('city') || '',
    state: formData.get('state') || '',
  };

  const workerUrl = Theme.infinitepay && Theme.infinitepay.workerUrl;
  if (!workerUrl) {
    showError(form, Theme.translations.infinitepay_error);
    return;
  }

  clearError(form);
  setSubmitLoading(form, true);

  try {
    const cart = await getCart();
    if (!cart.items || cart.items.length === 0) {
      // Distinct from the generic error below: this isn't a payment/network
      // failure, so the message tells the shopper what to actually do
      // instead of prompting a pointless retry.
      showError(form, Theme.translations.infinitepay_empty_cart);
      setSubmitLoading(form, false);
      return;
    }

    const items = cart.items.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
    }));

    const response = await fetch(`${workerUrl.replace(/\/$/, '')}/api/create-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, customer, address }),
    });
    const data = await response.json();

    if (!response.ok || !data.payment_url) {
      throw new Error(data.error || `unexpected response (${response.status})`);
    }

    saveForm({ ...customer, ...address });
    window.location.href = data.payment_url;
  } catch (error) {
    console.error('InfinitePay checkout failed:', error);
    showError(form, Theme.translations.infinitepay_error);
    setSubmitLoading(form, false);
  }
}

/* ---------------------------- boot ---------------------------- */

const form = document.querySelector(FORM_SELECTOR);
if (form) {
  prefillForm(form);
  initCpfField(form);
  initCepField(form);
  initSectionNavigation(form);

  // A returning shopper's saved CEP is already in the field after
  // prefillForm() - resolve it immediately instead of waiting for them to
  // retype it, so Section 2 isn't stuck showing just the CEP field.
  const cepField = form.elements.namedItem('cep');
  if (cepField instanceof HTMLInputElement && cepField.value) {
    handleCepInput(form);
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    submitCheckout(form);
  });
}
